// app/routes/thoughts.$thoughtId.tsx

import { type LoaderFunction, type MetaFunction, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { prisma } from '#app/utils/db.server.js'

type LoaderData = {
	thought: {
		id: string
		tweetId: string
		fullText: string
		createdAt: string
		retweetCount: number
		favoriteCount: number
		source: string | null
		language: string | null
	}
}

export const loader: LoaderFunction = async ({ params }) => {
	const thought = await prisma.thought.findUnique({
		where: { id: params.thoughtId },
		select: {
			id: true,
			tweetId: true,
			fullText: true,
			createdAt: true,
			retweetCount: true,
			favoriteCount: true,
			source: true,
			language: true,
		},
	})

	if (!thought) {
		throw new Response('Not Found', { status: 404 })
	}

	return json({ thought })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const typed = data as LoaderData | undefined
	if (!typed) {
		return [{ title: 'Thought Not Found - Barry McGee' }]
	}

	const thoughtPreview =
		typed.thought.fullText.length > 100
			? typed.thought.fullText.slice(0, 97) + '...'
			: typed.thought.fullText

	return [
		{ title: `Thought - Barry McGee` },
		{
			name: 'description',
			content: thoughtPreview,
		},
		{ property: 'og:title', content: 'Thought - Barry McGee' },
		{
			property: 'og:description',
			content: thoughtPreview,
		},
		{
			property: 'og:image',
			content: 'https://barrymcgee.dev/img/speaking.jpeg',
		},
		{ property: 'og:type', content: 'article' },
		{
			property: 'og:url',
			content: `https://barrymcgee.dev/thoughts/${typed.thought.id}`,
		},
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: '@barrymcgee' },
		{ name: 'twitter:creator', content: '@barrymcgee' },
	]
}

export default function ThoughtRoute() {
	const { thought } = useLoaderData<LoaderData>()
	const [decodedText, setDecodedText] = useState(thought.fullText)

	useEffect(() => {
		function decodeHtmlEntities(text: string) {
			const textArea = document.createElement('textarea')
			textArea.innerHTML = text
			return textArea.value
		}

		setDecodedText(decodeHtmlEntities(thought.fullText))
	}, [thought.fullText])

	function linkifyText(text: string) {
		const urlRegex = /(https?:\/\/\S+)/gi
		const mentionRegex = /(@\w+)/g

		const parts = text.split(/(\s+)/).map((part, index) => {
			if (urlRegex.test(part)) {
				return (
					<a key={index} href={part} target="_blank" rel="noopener noreferrer">
						{part}
					</a>
				)
			} else if (mentionRegex.test(part)) {
				const username = part.slice(1)
				return (
					<a
						key={index}
						href={`https://twitter.com/${username}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						{part}
					</a>
				)
			} else {
				return part
			}
		})

		return <>{parts}</>
	}

	const retweetIcon = (
		<svg
			width="24px"
			height="24px"
			strokeWidth="1.5"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			color="#9ca3af"
		>
			<path
				d="M17 17H8C6.33333 17 3 16 3 12C3 8 6.33333 7 8 7H16C17.6667 7 21 8 21 12C21 13.4943 20.5348 14.57 19.865 15.3312"
				stroke="#9ca3af"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			></path>
			<path
				d="M14.5 14.5L17 17L14.5 19.5"
				stroke="#9ca3af"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			></path>
		</svg>
	)

	const favIconEmpty = (
		<svg
			width="20px"
			height="20px"
			strokeWidth="1.5"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			color="#9ca3af"
		>
			<path
				d="M22 8.86222C22 10.4087 21.4062 11.8941 20.3458 12.9929C17.9049 15.523 15.5374 18.1613 13.0053 20.5997C12.4249 21.1505 11.5042 21.1304 10.9488 20.5547L3.65376 12.9929C1.44875 10.7072 1.44875 7.01723 3.65376 4.73157C5.88044 2.42345 9.50794 2.42345 11.7346 4.73157L11.9998 5.00642L12.2648 4.73173C13.3324 3.6245 14.7864 3 16.3053 3C17.8242 3 19.2781 3.62444 20.3458 4.73157C21.4063 5.83045 22 7.31577 22 8.86222Z"
				stroke="#9ca3af"
				strokeWidth="1.5"
				strokeLinejoin="round"
			></path>
		</svg>
	)

	const favIconFull = (
		<svg
			width="20px"
			height="20px"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			color="#9ca3af"
			strokeWidth="1.5"
		>
			<path
				fill-rule="evenodd"
				clipRule="evenodd"
				d="M11.9999 3.94228C13.1757 2.85872 14.7069 2.25 16.3053 2.25C18.0313 2.25 19.679 2.95977 20.8854 4.21074C22.0832 5.45181 22.75 7.1248 22.75 8.86222C22.75 10.5997 22.0831 12.2728 20.8854 13.5137C20.089 14.3393 19.2938 15.1836 18.4945 16.0323C16.871 17.7562 15.2301 19.4985 13.5256 21.14L13.5216 21.1438C12.6426 21.9779 11.2505 21.9476 10.409 21.0754L3.11399 13.5136C0.62867 10.9374 0.62867 6.78707 3.11399 4.21085C5.54605 1.68984 9.46239 1.60032 11.9999 3.94228Z"
				fill="#9ca3af"
			></path>
		</svg>
	)

	return (
		<div>
			<h1 className="text-2xl">Thoughts from the archive</h1>
			<div className="thought-detail rounded-xl border p-6 shadow-md">
				<p className="thought-text text-[1.3rem]">{linkifyText(decodedText)}</p>
				<div className="thought-metadata">
					<div className="flex gap-3 text-sm text-gray-400">
						<p>{new Date(thought.createdAt).toLocaleString()} |</p>
						<p className="flex gap-1">
							<span>{retweetIcon}</span>
							<span>{thought.retweetCount}</span>
						</p>
						<p className="flex gap-1">
							<span className="pt-1">
								{thought.favoriteCount > 0 ? favIconFull : favIconEmpty}{' '}
							</span>
							<span>{thought.favoriteCount}</span>
						</p>
					</div>
				</div>
				<a
					href={`https://x.com/barrymcgee/status/${thought.tweetId}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					View original tweet
				</a>
			</div>
		</div>
	)
}
